import React, { useEffect } from 'react';

import {
  Button,
  Form,
  Input,
  Radio,
  InputNumber,
  Select,
  DatePicker,
  Space,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';

import {
  WrapperSelect,
  NumberTextInput,
  BrickCodeInput,
} from 'common/components';

import { useGetTargetMarketList } from 'hooks/createProductHooks';

import sortByKey from 'utils/sortByKey';
import {
  getProductDescriptionValidationRules,
  getRestrictedTextValidationRules,
  validateGTIN,
  validateUPC12,
  validateUPC13,
  validateUPCE,
} from 'common/components/product-add/components/utils/validation';
import {
  LENGTH_PRODUCT_DESCRIPTION,
  LENGTH_PRODUCT_NAME,
  MAX_NUMBER_INPUT_UPC,
} from 'common/components/product-add/components/constants';
import { RequiredProductImagery } from 'common/components/product-add/make-product-active/RequiredProductImagery';
import { RequiredBrand } from 'common/components/product-add/make-product-active/RequiredBrand';
import { lowercaseFirstChar } from 'hooks/useValidateSelfLoadedProduct';
import { filterMinimumRequirements } from 'pages/home/ribbon/components/sections/product/components/make-product-active/utils';

const MinimumRequirementPropertiesInputProduct = (props) => {
  const {
    item,
    updateFormValue,
    hasBrickCode,
    primaryErrors,
    minimumRequirementErrorFields,
    entityData,
  } = props;

  const form = Form.useFormInstance();
  const inputSchema = item?.inputSchema;
  const fullPathDisplayName = item?.fullPathDisplayName;

  const targetMarketList = useGetTargetMarketList();

  // Set initial value from entityData
  useEffect(() => {
    if (entityData && inputSchema?.name) {
      const fieldName = inputSchema.name;
      const fieldValue = entityData[lowercaseFirstChar(fieldName)];

      if (fieldValue !== undefined && form) {
        form.setFieldsValue({
          [fieldName]: fieldValue,
        });
      }
    }
  }, [entityData, inputSchema?.name, form]);

  // Set errors from validation results
  useEffect(() => {
    if (!inputSchema?.name || !form) return;

    const fieldName = inputSchema.name;
    const errorMessages = [];

    // Check for primary errors (with custom error messages)
    if (primaryErrors && primaryErrors.length > 0) {
      const primaryFieldErrors = primaryErrors.filter(
        (error) => error?.fieldName?.toLowerCase() === fieldName.toLowerCase()
      );

      primaryFieldErrors.forEach((error) => {
        if (error.errorMessage) {
          errorMessages.push(error.errorMessage);
        }
      });
    }

    // Check for minimum requirement errors (show "FieldName is required")
    if (
      minimumRequirementErrorFields &&
      minimumRequirementErrorFields.length > 0
    ) {
      const hasMinimumRequirementError = minimumRequirementErrorFields.some(
        (errorField) => errorField?.toLowerCase() === fieldName.toLowerCase()
      );

      if (hasMinimumRequirementError) {
        // Filter out minimum requirement errors that are also in primary errors
        const filteredMinimumRequirements = filterMinimumRequirements(
          minimumRequirementErrorFields,
          primaryErrors ?? []
        );

        const isInFilteredList =
          filteredMinimumRequirements.includes(fieldName);

        // Only add the "required" message if it's in the filtered list
        // (meaning it's not already covered by a primary error)
        if (isInFilteredList) {
          errorMessages.push(`${fullPathDisplayName} is required`);
        }
      }
    }

    // Set all collected errors or clear if none
    if (errorMessages.length > 0) {
      form.setFields([
        {
          name: [fieldName],
          errors: errorMessages,
        },
      ]);
    } else {
      form.setFields([
        {
          name: [fieldName],
          errors: [],
        },
      ]);
    }
  }, [
    primaryErrors,
    inputSchema.name,
    form,
    minimumRequirementErrorFields,
    fullPathDisplayName,
    inputSchema,
  ]);

  // Check if field is in minimum requirement error fields
  const hasMinimumRequirementError =
    minimumRequirementErrorFields &&
    inputSchema?.name &&
    minimumRequirementErrorFields.some(
      (errorField) =>
        errorField?.toLowerCase() === inputSchema.name?.toLowerCase()
    );

  const numberFormat = () => {
    return {
      formatter: (value) =>
        `${value}${inputSchema.format ? inputSchema.format : ''}`,
      parser: (value) => value.replace(inputSchema.format, ''),
    };
  };

  const handleNumberInput = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const renderSelectInput = () => {
    return (
      <WrapperSelect
        mode={inputSchema.mode}
        tagRender={inputSchema.tagRender}
        showSearch={true}
        getPopupContainer={() =>
          document.getElementById('validate-minimum-requirement')
        }
      >
        {sortByKey(inputSchema.options, 'label')?.map((option, index) => {
          const { value, label, icon } = option;
          return (
            <Select.Option key={inputSchema.name + index} value={value}>
              {option.icon && (
                <img
                  style={{ width: 20, height: 20, marginRight: 4 }}
                  src={icon}
                  alt={label}
                />
              )}
              {label}
            </Select.Option>
          );
        })}
      </WrapperSelect>
    );
  };

  const renderListInput = (inputFieldData) => {
    return (
      <Form.List name={inputFieldData.name}>
        {(fields, { add, remove }) => {
          return (
            <>
              <div>
                {fields.map((field) => (
                  <Space key={field.key} align='baseline'>
                    <Form.Item {...field}>
                      {inputType[inputFieldData.type]}
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                ))}
              </div>
              <div>
                <Button
                  type='dashed'
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Add value
                </Button>
              </div>
            </>
          );
        }}
      </Form.List>
    );
  };

  const renderInput = () => {
    return <Input />;
  };

  const getFormItemRules = () => {
    const rules = [];

    // Add required rule if field is in minimum requirement error fields
    if (hasMinimumRequirementError) {
      rules.push({
        required: true,
        message: `${fullPathDisplayName} is required`,
      });
    }

    return rules;
  };

  const renderUpc12 = () => {
    return (
      <Form.Item
        style={{ marginRight: 12 }}
        name='upc12'
        validateTrigger={['onBlur', 'onChange']}
        rules={[
          ...getFormItemRules(),
          {
            validator: (_, value) => {
              if (!value) {
                return Promise.resolve();
              }
              const validation = validateUPC12(value);
              if (!validation.isValid) {
                return Promise.reject(new Error(validation.error));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          min='0'
          onKeyPress={handleNumberInput}
          maxLength={MAX_NUMBER_INPUT_UPC}
        />
      </Form.Item>
    );
  };

  const renderGtin = () => {
    return (
      <Form.Item
        style={{ marginRight: 12 }}
        name='gtin'
        validateTrigger={['onBlur', 'onChange']}
        rules={[
          ...getFormItemRules(),
          {
            validator: (_, value) => {
              const validation = validateGTIN(value);
              if (!validation.isValid) {
                return Promise.reject(new Error(validation.error));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          min='0'
          onKeyPress={handleNumberInput}
          maxLength={MAX_NUMBER_INPUT_UPC}
        />
      </Form.Item>
    );
  };

  const renderUpc8 = () => {
    return (
      <Form.Item
        style={{ marginRight: 12 }}
        name='upc8'
        validateTrigger={['onBlur', 'onChange']}
        rules={[
          ...getFormItemRules(),
          {
            validator: (_, value) => {
              const validation = validateUPCE(value);
              if (!validation.isValid) {
                return Promise.reject(new Error(validation.error));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          min='0'
          onKeyPress={handleNumberInput}
          maxLength={MAX_NUMBER_INPUT_UPC}
        />
      </Form.Item>
    );
  };

  const renderUpc13 = () => {
    return (
      <Form.Item
        style={{ marginRight: 12 }}
        name='upc13'
        validateTrigger={['onBlur', 'onChange']}
        rules={[
          ...getFormItemRules(),
          {
            validator: (_, value) => {
              const validation = validateUPC13(value);
              if (!validation.isValid) {
                return Promise.reject(new Error(validation.error));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          min='0'
          onKeyPress={handleNumberInput}
          maxLength={MAX_NUMBER_INPUT_UPC}
        />
      </Form.Item>
    );
  };

  const renderProductDescription = () => {
    return (
      <Form.Item
        name='productDescription'
        style={{ marginBottom: 16 }}
        rules={[
          ...getProductDescriptionValidationRules(),
          ...getFormItemRules(),
        ]}
      >
        <Input.TextArea
          rows={4}
          maxLength={LENGTH_PRODUCT_DESCRIPTION}
          showCount={true}
        />
      </Form.Item>
    );
  };

  const renderProductName = () => {
    return (
      <Form.Item
        name='productName'
        style={{ marginBottom: 16 }}
        rules={[
          ...getRestrictedTextValidationRules('Product Name'),
          ...getFormItemRules(),
        ]}
      >
        <Input maxLength={LENGTH_PRODUCT_NAME} showCount={true} />
      </Form.Item>
    );
  };

  const renderCustomInput = () => {
    const customInputComponents = {
      targetMarket: renderTargetMarketInput(targetMarketList),
      brickCode: renderBrickCode(updateFormValue),
      brickCodeName: renderBrickCodeName({ updateFormValue, hasBrickCode }),
      StateOfIncorporation: renderInput,
    };

    return (
      customInputComponents[inputSchema.name] &&
      customInputComponents[inputSchema.name]()
    );
  };

  const inputType = inputSchema
    ? {
        text: <Input />,
        checkbox: (
          <Radio.Group>
            <Radio value={true}>True</Radio>
            <Radio value={false}>False</Radio>
          </Radio.Group>
        ),
        number: <InputNumber {...numberFormat()} min={inputSchema.min} />,
        select: renderSelectInput(),
        datePicker: (
          <DatePicker
            format={inputSchema.dateFormat}
            getPopupContainer={() =>
              document.getElementById('validate-minimum-requirement')
            }
            style={{ width: '100%' }}
          />
        ),
        numberText: <NumberTextInput />,
        custom: renderCustomInput(),
        selectEnum: renderSelectInput(),
      }
    : {};

  const renderFormItemElement = (inputSchema) => {
    if (inputSchema?.name?.toLowerCase() === 'gtin') {
      return renderGtin();
    }
    if (inputSchema?.name?.toLowerCase() === 'upc8') {
      return renderUpc8();
    }
    if (inputSchema?.name?.toLowerCase() === 'upc12') {
      return renderUpc12();
    }
    if (inputSchema?.name?.toLowerCase() === 'upc13') {
      return renderUpc13();
    }

    if (item?.dataType?.toLowerCase() === 'image') {
      return (
        <RequiredProductImagery
          fieldFullPath={item.fieldFullPath}
          displayName={item.displayName}
          primaryErrors={primaryErrors}
          mode='add'
          entityId={entityData?.id}
        />
      );
    }
    if (item?.fieldName?.toLowerCase() === 'brandid') {
      return <RequiredBrand formInstance={form} />;
    }

    if (item?.fieldName?.toLowerCase() === 'productdescription') {
      return renderProductDescription();
    }

    if (item?.fieldName?.toLowerCase() === 'productname') {
      return renderProductName();
    }

    return (
      <Form.Item
        name={inputSchema.name}
        valuePropName={inputSchema.type === 'checkbox' ? 'checked' : undefined}
        rules={getFormItemRules()}
      >
        {inputSchema.isList
          ? renderListInput(inputSchema)
          : inputType[inputSchema.type]}
      </Form.Item>
    );
  };

  return inputSchema ? renderFormItemElement(inputSchema) : null;
};

const renderTargetMarketInput = (targetMarketList) => () => {
  const sortTargetMarket = () => {
    if (!targetMarketList) return [];
    const USMarket = targetMarketList.find((marketItem, index) => {
      if (marketItem.Numeric !== 840) return false;

      return true;
    });

    const noneUSMarketList = targetMarketList.filter((marketItem) => {
      return marketItem.Numeric !== 840;
    });

    const sortedNoneUSMarketList = noneUSMarketList.sort((a, b) => {
      return a.Country - b.Country;
    });

    return [USMarket, ...sortedNoneUSMarketList];
  };

  const sortedTargetMarket = sortTargetMarket();

  return (
    <WrapperSelect
      getPopupContainer={() =>
        document.getElementById('validate-minimum-requirement')
      }
      allowClear
      showSearch
      filterOption={(input, option) =>
        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
      }
    >
      {targetMarketList?.length > 0 &&
        sortedTargetMarket?.map((item, index) => (
          <Select.Option key={index} value={item?.Country}>
            {item?.Country}
          </Select.Option>
        ))}
    </WrapperSelect>
  );
};

const renderBrickCode = (updateFormValue) => () => {
  const updateBrickCodeData = (brick) => {
    updateFormValue({
      brickCode: brick.brickCode || null,
      brickCodeName: brick.brickCodeName || null,
    });
  };

  return <BrickCodeInput updateBrickCodeData={updateBrickCodeData} />;
};

const renderBrickCodeName =
  ({ updateFormValue, hasBrickCode }) =>
  () => {
    return hasBrickCode ? (
      <Input disabled />
    ) : (
      <>
        <Form.Item name='brickCode' hidden>
          render hidden input to collect brick code
        </Form.Item>
        {renderBrickCode(updateFormValue)()}
      </>
    );
  };

export default MinimumRequirementPropertiesInputProduct;
